import type { FiltersGQL, UnusedAssetsFiltersGQL } from 'modules/filters/model';
import type { DayOption } from 'modules/usage/hoursTracking/types';
import type { FetchApi } from '../client';

type SortBy = {
  field: string;
  order: string;
};

type ExportFleetDataParams = {
  since: Date | string;
  until: Date | string;
  assetFilters: FiltersGQL;
  sortBy?: SortBy;
  fileType: string;
  search: string;
  columns: string[];
  extraFilters?: {
    includeAssetsWithNoData?: boolean;
  };
};

type ExportDailyUsageHoursParams = {
  since: Date | string;
  until: Date | string;
  fileType: string;
};

type ExportUsageHoursTrackingParams = {
  since: Date | string;
  until: Date | string;
  columns: string[];
  search: string;
  assetFilters: FiltersGQL;
  daysOfWeek: DayOption[];
  exportAnnotations: boolean;
};

type RequestExportParams = {
  assetFilters: FiltersGQL;
  columns: string[];
  since: Date;
  until: Date;
  fileType: string;
};

type RequestExportEvParams = {
  assetFilters: FiltersGQL;
  columns: string[];
  since: Date;
  until: Date;
  search: string | undefined;
  extraFilters: {
    detailed: boolean;
  };
};

type RequestExportUnusedAssetsParams = {
  assetFilters: FiltersGQL;
  unusedAssetsFilters: UnusedAssetsFiltersGQL;
  columns: string[];
};

export type RequestExportProductivityAssetsPerformanceParams = {
  assetFilters: FiltersGQL;
  columns: string[];
  since: Date;
  until: Date;
  extraFilters: {
    detailed: boolean;
  };
};

export type RequestExportProductivityAssetsListParams = {
  assetFilters: FiltersGQL;
  columns: string[];
  since: Date;
  until: Date;
  search: string | undefined;
  extraFilters: {
    detailed: boolean;
  };
};

type RequestExportParamsDataSource = {
  assetFilters: FiltersGQL;
  dataSource: 'daily' | 'monthly';
  since: Date;
  until: Date;
  fileType: string;
  excludeAnnotatedDays?: boolean;
  includeAssetsWithNoData?: boolean;
};

export const analyticsApi = (fetchApi: FetchApi) => ({
  downloadInspectionPdf(
    token: string,
    inspectionId: number,
    onCompleted: (res: Response) => void,
    onError: () => void,
  ): Promise<void> {
    const endpoint = fetchApi.generateEndpoint(
      `/app/inspections/${inspectionId}/pdf/download`,
    );

    return fetch(endpoint, {
      method: 'GET',
      headers: {
        'x-access-token': token,
      },
    })
      .then(onCompleted)
      .catch(onError);
  },

  // IDLE RATE REPORT
  requestExportIdleRateAssetsPerformance(
    token: string,
    params: RequestExportParams,
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post('/app/analytics/usage/idle_rate/assets_performance/export', {
        ...params,
      })
      .catch((errorMessage) => {
        throw new Error(
          (typeof errorMessage === 'string' && errorMessage) ||
            'An error occurred while requesting your export. Please try again later',
        );
      });
  },
  requestExportIdleRateEnterprisesPerformance(
    token: string,
    params: RequestExportParams,
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post('/app/analytics/usage/idle_rate/enterprises_performance/export', {
        ...params,
      })
      .catch((errorMessage) => {
        throw new Error(
          (typeof errorMessage === 'string' && errorMessage) ||
            'An error occurred while requesting your export. Please try again later',
        );
      });
  },
  async requestExportIdleRateAssetList(
    token: string,
    params: RequestExportParamsDataSource,
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post('/app/analytics/usage/idle_rate/asset_list/export', {
        ...params,
      })
      .catch((errorMessage) => {
        throw new Error(
          (typeof errorMessage === 'string' && errorMessage) ||
            'An error occurred while requesting your export. Please try again later',
        );
      });
  },

  // HOURLY CONSUMPTION REPORT
  requestExportHourlyConsumptionAssetsPerformance(
    token: string,
    params: RequestExportParams,
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post('/app/analytics/hourly_consumption/assets_performance/export', {
        ...params,
      })
      .catch((errorMessage) => {
        throw new Error(
          (typeof errorMessage === 'string' && errorMessage) ||
            'An error occurred while requesting your export. Please try again later',
        );
      });
  },

  requestExportHourlyConsumptionEnterprisesPerformance(
    token: string,
    params: RequestExportParams,
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post(
        '/app/analytics/hourly_consumption/enterprises_performance/export',
        {
          ...params,
        },
      )
      .catch((errorMessage) => {
        throw new Error(
          (typeof errorMessage === 'string' && errorMessage) ||
            'An error occurred while requesting your export. Please try again later',
        );
      });
  },

  requestExportHourlyConsumptionAssetList(
    token: string,
    params: RequestExportParamsDataSource,
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post('/app/analytics/hourly_consumption/asset_list/export', {
        ...params,
      })
      .catch((errorMessage) => {
        throw new Error(
          (typeof errorMessage === 'string' && errorMessage) ||
            'An error occurred while requesting your export. Please try again later',
        );
      });
  },

  requestExportCarbonEmissionsAssetsPerformance(
    token: string,
    params: RequestExportParams,
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post('/app/analytics/co2emission/assetsperformance/export', {
        ...params,
      })
      .catch((errorMessage) => {
        throw new Error(
          (typeof errorMessage === 'string' && errorMessage) ||
            'An error occurred while requesting your export. Please try again later',
        );
      });
  },

  requestExportCarbonEmissionsAssetList(
    token: string,
    params: RequestExportParamsDataSource,
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post('/app/analytics/co2emission/assetlist/export', {
        ...params,
      })
      .catch((errorMessage) => {
        throw new Error(
          (typeof errorMessage === 'string' && errorMessage) ||
            'An error occurred while requesting your export. Please try again later',
        );
      });
  },

  requestExportUsageRateAssetsPerformance(
    token: string,
    params: RequestExportParams,
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post('/app/analytics/usage/usage_rate/assets_performance/export', {
        ...params,
      })
      .catch((errorMessage) => {
        throw new Error(
          (typeof errorMessage === 'string' && errorMessage) ||
            'An error occurred while requesting your export. Please try again later',
        );
      });
  },

  requestExportUsageRateAssetList(
    token: string,
    params: RequestExportParamsDataSource,
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post('/app/analytics/usage/usage_rate/asset_list/export', {
        ...params,
      })
      .catch((errorMessage) => {
        throw new Error(
          (typeof errorMessage === 'string' && errorMessage) ||
            'An error occurred while requesting your export. Please try again later',
        );
      });
  },

  requestExportFaultsDistributed(
    token: string,
    params: Partial<{
      since: Date | string;
      until: Date | string;
      assetFilters: FiltersGQL;
      severities: number[];
      search: string;
      columns: string[];
      sortBy: SortBy;
    }> & { fileType: string },
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post('/app/analytics/faults/export_distributed', params)
      .catch((errorMessage) => {
        throw new Error(
          (typeof errorMessage === 'string' && errorMessage) ||
            'An error occurred while requesting your export. Please try again later',
        );
      });
  },

  requestExportFaultsGroupByAssetDistributed(
    token: string,
    params: Partial<{
      since: Date | string;
      until: Date | string;
      assetFilters: FiltersGQL;
      severities: number[];
      search: string;
      columns: string[];
      sortBy: SortBy;
    }> & { fileType: string },
  ): Promise<void> {
    return fetchApi
      .setToken(token)
      .post('/app/analytics/faults/export_group_by_asset_distributed', params)
      .catch((errorMessage) => {
        throw new Error(
          (typeof errorMessage === 'string' && errorMessage) ||
            'An error occurred while requesting your export. Please try again later',
        );
      });
  },

  async requestExportCumulativeFleetDataDistributed(
    token: string,
    params: ExportFleetDataParams,
    exportBIMode: boolean,
  ): Promise<void> {
    const url = exportBIMode
      ? '/app/analytics/fleetdata/cumulative/export/bi'
      : '/app/analytics/fleetdata/cumulative/export_distributed';
    try {
      return await fetchApi.setToken(token).post(url, params);
    } catch (errorMessage) {
      throw new Error(
        (typeof errorMessage === 'string' && errorMessage) ||
          'An error occurred while requesting your export. Please try again later',
      );
    }
  },

  async requestExportFleetUsage(
    token: string,
    // we are using same params as fleet data export
    params: ExportFleetDataParams,
  ): Promise<void> {
    try {
      return await fetchApi
        .setToken(token)
        .post('/app/analytics/fleet_usage/export', params);
    } catch (errorMessage) {
      throw new Error(
        (typeof errorMessage === 'string' && errorMessage) ||
          'An error occurred while requesting your export. Please try again later',
      );
    }
  },

  async requestExportHistoryFleetDataDistributed(
    token: string,
    params: ExportFleetDataParams,
    exportBIMode: boolean,
  ): Promise<void> {
    const url = exportBIMode
      ? '/app/analytics/fleetdata/history/export/bi'
      : '/app/analytics/fleetdata/history/export_distributed';
    try {
      return await fetchApi.setToken(token).post(url, params);
    } catch (errorMessage) {
      throw new Error(
        (typeof errorMessage === 'string' && errorMessage) ||
          'An error occurred while requesting your export. Please try again later',
      );
    }
  },

  async requestExportDailyUsageHours(
    token: string,
    params: ExportDailyUsageHoursParams,
  ): Promise<void> {
    try {
      return await fetchApi
        .setToken(token)
        .post('/app/analytics/daily_usage_hours/export', params);
    } catch (errorMessage) {
      throw new Error(
        (typeof errorMessage === 'string' && errorMessage) ||
          'An error occurred while requesting your export. Please try again later',
      );
    }
  },

  async requestExportUsageHoursTracking(
    token: string,
    params: ExportUsageHoursTrackingParams,
  ): Promise<void> {
    try {
      return await fetchApi
        .setToken(token)
        .post('/app/analytics/usage/hours_tracking/export', {
          ...params,
          fileType: 'XLSX',
        });
    } catch (errorMessage) {
      throw new Error(
        (typeof errorMessage === 'string' && errorMessage) ||
          'An error occurred while requesting your export. Please try again later',
      );
    }
  },

  async requestExportEvReport(
    token: string,
    params: RequestExportEvParams,
  ): Promise<void> {
    try {
      return await fetchApi.setToken(token).post('/app/analytics/ev/export', {
        ...params,
        fileType: 'XLSX',
      });
    } catch (errorMessage) {
      throw new Error(
        (typeof errorMessage === 'string' && errorMessage) ||
          'An error occurred while requesting your export. Please try again later',
      );
    }
  },

  async requestExportUnusedAssets(
    token: string,
    params: RequestExportUnusedAssetsParams,
  ): Promise<void> {
    try {
      return await fetchApi
        .setToken(token)
        .post('/app/analytics/usage/unused/export', {
          ...params,
          fileType: 'XLSX',
        });
    } catch (errorMessage) {
      throw new Error(
        (typeof errorMessage === 'string' && errorMessage) ||
          'An error occurred while requesting your export. Please try again later',
      );
    }
  },

  async requestExportProductivityAssetsPerformance(
    token: string,
    params: RequestExportProductivityAssetsPerformanceParams,
  ): Promise<void> {
    try {
      return await fetchApi
        .setToken(token)
        .post('/app/analytics/usage/productivity/assets_performance/export', {
          ...params,
          fileType: 'XLSX',
        });
    } catch (errorMessage) {
      throw new Error(
        (typeof errorMessage === 'string' && errorMessage) ||
          'An error occurred while requesting your export. Please try again later',
      );
    }
  },

  async requestTableExportProductivityAssetsList(
    token: string,
    params: RequestExportProductivityAssetsListParams,
  ): Promise<void> {
    try {
      return await fetchApi
        .setToken(token)
        .post('/app/analytics/usage/productivity/assets_list/export', {
          ...params,
          fileType: 'XLSX',
        });
    } catch (errorMessage) {
      throw new Error(
        (typeof errorMessage === 'string' && errorMessage) ||
          'An error occurred while requesting your export. Please try again later',
      );
    }
  },
});
